import { useCallback, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useSession } from '../../lib/useSession';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Chip } from '../../components/Chip';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { errorMessage, useLoad } from '../members/hooks';
import { ROLE_LABEL } from '../members/roles';
import { domainQuery, PRIMARY_DOMAIN, type Slot, type SlotList } from './types';

interface HomeData {
  custody: Slot[];
  activated: Slot[];
}

function activatePath(slot: Slot): string {
  return `/terreno/activar/${encodeURIComponent(slot.code)}${domainQuery(slot.domain)}`;
}

function matches(slot: Slot, needle: string): boolean {
  if (!needle) return true;
  const n = needle.toLowerCase();
  return (
    slot.code.toLowerCase().startsWith(n) ||
    (slot.business?.name ?? '').toLowerCase().includes(n) ||
    (slot.business?.externalRef ?? '').toLowerCase() === n ||
    (slot.lotId ?? '').toLowerCase().includes(n)
  );
}

export function FieldHomePage() {
  const { activeOrg, user } = useSession();
  const orgId = activeOrg?.orgId;
  const uid = user?.uid;
  const loader = useCallback(async (): Promise<HomeData> => {
    const custodyQs = new URLSearchParams({ state: 'en_custodia', limit: '200' });
    if (uid) custodyQs.set('custodianUid', uid);
    const [custody, activated] = await Promise.all([
      api.get<SlotList>(`/v1/qrs?${custodyQs}`, { orgId }),
      api.get<SlotList>('/v1/qrs?state=activado&limit=200', { orgId }),
    ]);
    return {
      custody: custody.items,
      // Owner/Admin ven toda la organización: en terreno solo interesan los que activó esta persona.
      activated: activated.items.filter((s) => s.activatedBy === uid),
    };
  }, [orgId, uid]);
  const { data, error, loading, reload } = useLoad(loader);

  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Slot | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const closeEdit = useCallback(() => setEditing(null), []);

  const custody = useMemo(() => (data?.custody ?? []).filter((s) => matches(s, search.trim())), [data, search]);
  const activated = useMemo(
    () =>
      [...(data?.activated ?? [])]
        .filter((s) => matches(s, search.trim()))
        .sort((a, b) => (b.activatedAt ?? '').localeCompare(a.activatedAt ?? ''))
        .slice(0, 20),
    [data, search],
  );

  return (
    <div className="space-y-5">
      <div>
        <p className="font-display text-lg font-bold text-ink">Hola{user?.email ? `, ${user.email.split('@')[0]}` : ''}</p>
        <p className="text-sm text-ink-muted">
          {activeOrg?.orgName} · {activeOrg ? ROLE_LABEL[activeOrg.role] : ''}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-line bg-white p-3">
          <p className="text-xs text-ink-muted">En mi custodia</p>
          <p className="font-display text-2xl font-bold tabular-nums text-ink">{loading && !data ? '—' : data?.custody.length ?? 0}</p>
        </div>
        <div className="rounded-lg border border-line bg-white p-3">
          <p className="text-xs text-ink-muted">Activé</p>
          <p className="font-display text-2xl font-bold tabular-nums text-ink">{loading && !data ? '—' : data?.activated.length ?? 0}</p>
        </div>
      </div>

      <Link to="/terreno/activar" className="flex min-h-12 w-full items-center justify-center rounded-md bg-action px-5 py-3 text-base font-semibold text-ink no-underline hover:bg-action-hover">
        Activar un QR
      </Link>

      <div aria-live="polite">{notice && <Banner tone="success">{notice}</Banner>}</div>
      {error && <Banner tone="error" action={<Button variant="secondary" size="sm" onClick={() => void reload()}>Reintentar</Button>}>{error}</Banner>}

      <Input label="Buscar por código, negocio o lote" type="search" value={search} onChange={(e) => setSearch(e.target.value)} />

      <section aria-labelledby="custodia-titulo" className="space-y-2">
        <h2 id="custodia-titulo" className="font-display text-base font-bold text-ink">Mis QR en custodia</h2>
        {loading && !data ? (
          <div className="h-16 animate-pulse rounded-lg bg-gray-100" />
        ) : custody.length === 0 ? (
          <p className="rounded-lg border border-line bg-white p-4 text-sm text-ink-muted">
            {search ? 'Ningún QR en tu custodia coincide con la búsqueda.' : 'No tienes QR en custodia. Pídele a tu Admin que te asigne.'}
          </p>
        ) : (
          <ul className="divide-y divide-line rounded-lg border border-line bg-white">
            {custody.slice(0, 50).map((s) => (
              <li key={s.id}>
                <Link to={activatePath(s)} className="flex min-h-12 items-center justify-between gap-3 px-4 py-2 text-ink no-underline hover:bg-gray-50">
                  <span className="font-mono text-base font-semibold tracking-wider">{s.code}</span>
                  <span className="text-xs text-ink-muted">
                    {s.lotId ?? ''} {s.domain !== PRIMARY_DOMAIN ? `· ${s.domain}` : ''}
                  </span>
                  <span className="text-sm text-green">Activar</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="activados-titulo" className="space-y-2">
        <h2 id="activados-titulo" className="font-display text-base font-bold text-ink">Activados recientes</h2>
        {activated.length === 0 ? (
          <p className="text-sm text-ink-muted">Aún no activas QR.</p>
        ) : (
          <ul className="divide-y divide-line rounded-lg border border-line bg-white">
            {activated.map((s) => (
              <li key={s.id} className="flex min-h-12 items-center justify-between gap-3 px-4 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-ink">{s.business?.name ?? 'Sin nombre'}</p>
                  <p className="text-xs text-ink-muted">
                    <span className="font-mono">{s.code}</span> · {s.business?.externalRef ?? '—'}
                    {s.destStatus === 'pending' && <Chip tone="warning">En revisión</Chip>}
                  </p>
                </div>
                <Button variant="secondary" onClick={() => setEditing(s)} className="min-h-11">Editar destino</Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <EditDestinationModal
        slot={editing}
        orgId={orgId}
        onClose={closeEdit}
        onSaved={async (slot) => {
          setEditing(null);
          setNotice(`${slot.code} ahora lleva a ${slot.targetUrl}. Aplica desde el próximo escaneo.`);
          await reload();
        }}
      />
    </div>
  );
}

function EditDestinationModal({ slot, orgId, onClose, onSaved }: { slot: Slot | null; orgId: string | undefined; onClose: () => void; onSaved: (slot: Slot) => Promise<void> }) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [lastSlot, setLastSlot] = useState<Slot | null>(null);

  if (slot && slot !== lastSlot) {
    setLastSlot(slot);
    setUrl(slot.targetUrl ?? '');
    setError(null);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!slot) return;
    if (!/^https?:\/\/\S+$/i.test(url.trim())) {
      setError('El destino debe empezar con https:// o http://.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await api.patch<Slot>(`/v1/qrs/${encodeURIComponent(slot.code)}/destination${domainQuery(slot.domain)}`, { targetUrl: url.trim() }, { orgId });
      await onSaved(saved);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'destination_flagged') setError('Este enlace está marcado como peligroso. No podemos usarlo como destino.');
      else setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={slot !== null} onClose={onClose} title={`Editar destino · ${slot?.code ?? ''}`} size="sm">
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <p className="text-sm text-ink-muted">{slot?.business?.name} · {slot?.business?.externalRef ?? 'sin referencia'}</p>
        <Input label="Destino" type="url" inputMode="url" value={url} onChange={(e) => setUrl(e.target.value)} error={error ?? undefined} />
        <p className="text-xs text-ink-muted">El cambio se aplica desde el próximo escaneo. El código y la imagen del QR no cambian.</p>
        <Button type="submit" block size="lg" loading={saving}>Guardar</Button>
      </form>
    </Modal>
  );
}
